import { MessageCircle, Check, FileText, Mail } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { waMessage, DATA_SHEETS_EMAIL_LINK } from "@/data";

const products = [
  {
    id: "engine-oils",
    title: "Engine Oils",
    img: "/assets/gen-engine-oils.png",
    desc: "Premium heavy-duty diesel and petrol motor oils engineered for maximum protection and extended drain intervals.",
    specs: ["API SL / CI-4", "15W-40 · 10W-40 · 20W-50", "Group II Base Oils"],
  },
  {
    id: "hydraulic-oils",
    title: "Hydraulic Oils",
    img: "/assets/gen-hydraulic-oils.png",
    desc: "Industrial and earthmoving hydraulic fluids formulated for reliable performance in demanding systems.",
    specs: ["ISO VG 32 · 46 · 68 · 100", "DIN 51524 Part 2 HLP", "Anti-Wear Additives"],
  },
  {
    id: "gear-transmission-oils",
    title: "Gear & Transmission Oils",
    img: "/assets/gen-gear-oils.png",
    desc: "Extreme-pressure differential, gearbox and automatic transmission fluids with high shear stability.",
    specs: ["API GL-4 / GL-5", "ATF DEX III", "80W-90 · 85W-140"],
  },
  {
    id: "antifreeze-grease",
    title: "Antifreeze & Grease",
    img: "/assets/gen-antifreeze-grease.png",
    desc: "Heavy-duty radiator coolants and high-temperature lithium complex greases for every fleet.",
    specs: ["50/50 Premix & Concentrate", "EP2 / MP3 Lithium Grease", "Corrosion Inhibition"],
  },
];

export default function Products() {
  return (
    <section id="products" data-testid="products-section" className="relative py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          eyebrow="Our Products"
          title="BULK SUPPLY OF PREMIUM LUBRICANTS"
          sub="Unbeatable prices on outstanding quality performance oils — 100% virgin spec oil packed in 20L, 210L drums and 1000L IBC."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className="h-full">
              <article
                data-testid={`product-card-${p.id}`}
                className="group h-full flex flex-col bg-steel/60 border border-gold/10 rounded-2xl overflow-hidden hover:border-gold/40 hover:shadow-[0_20px_60px_-15px_rgba(229,169,60,0.25)] hover:-translate-y-2 transition-all duration-500"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={p.img}
                    alt={`Auto City Lubricants ${p.title}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-steel via-transparent to-transparent" />
                </div>
                <div className="flex flex-col flex-1 p-6">
                  <h3 className="font-heading font-extrabold uppercase text-2xl text-white tracking-tight">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed flex-1">{p.desc}</p>
                  <ul className="mt-4 space-y-2">
                    {p.specs.map((s) => (
                      <li key={s} className="flex items-center gap-2 text-xs text-zinc-300">
                        <Check size={13} className="text-gold shrink-0" strokeWidth={3} />
                        <span className="uppercase tracking-wider">{s}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    data-testid={`product-enquire-${p.id}`}
                    href={waMessage(`Hello Auto City Lubricants, I would like a bulk quote for ${p.title}.`)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-6 inline-flex items-center justify-center gap-2 border border-gold/40 text-gold text-xs font-bold uppercase tracking-widest px-5 py-3 rounded-full hover:bg-gold hover:text-obsidian transition-all duration-300"
                  >
                    <MessageCircle size={14} strokeWidth={2.5} /> Enquire
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div
            data-testid="data-sheets-banner"
            className="mt-10 bg-coal/80 border border-gold/20 rounded-2xl px-7 py-6 flex flex-col sm:flex-row items-center justify-between gap-5"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold shrink-0">
                <FileText size={20} strokeWidth={1.75} />
              </div>
              <div>
                <p className="font-heading font-extrabold uppercase text-xl text-white tracking-tight leading-none">
                  MSDS &amp; TDS Available on Request
                </p>
                <p className="mt-1.5 text-xs text-zinc-400">
                  Material Safety Data Sheets and Technical Data Sheets for every product.
                </p>
              </div>
            </div>
            <a
              data-testid="data-sheets-email-button"
              href={DATA_SHEETS_EMAIL_LINK}
              className="shrink-0 inline-flex items-center gap-2 bg-gold text-obsidian text-xs font-extrabold uppercase tracking-widest px-6 py-3.5 rounded-full hover:bg-gold-bright hover:shadow-[0_0_28px_rgba(229,169,60,0.45)] transition-all duration-300"
            >
              <Mail size={15} strokeWidth={2.5} /> Request by Email
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
